if (!customElements.get('gift-message')) {
  class GiftMessage extends HTMLElement {
    connectedCallback() {
      this.textarea = this.querySelector('textarea');
      this.counter = this.querySelector('[data-gift-message-counter]');

      if (!this.textarea || !this.counter) return;

      this.updateCounter = this.updateCounter.bind(this);
      this.updateCounterAfterPageRestore = this.updateCounterAfterPageRestore.bind(this);
      this.textarea.addEventListener('input', this.updateCounter);
      window.addEventListener('pageshow', this.updateCounterAfterPageRestore);
      this.updateCounter();
    }

    disconnectedCallback() {
      this.textarea?.removeEventListener('input', this.updateCounter);
      window.removeEventListener('pageshow', this.updateCounterAfterPageRestore);
    }

    updateCounterAfterPageRestore() {
      requestAnimationFrame(() => this.updateCounter());
    }

    updateCounter() {
      const remaining = this.textarea.maxLength - this.textarea.value.length;
      this.counter.textContent = this.dataset.counterTemplate.replace('[count]', remaining);
    }
  }

  customElements.define('gift-message', GiftMessage);
}

if (!customElements.get('gift-message-editor')) {
  class GiftMessageEditor extends HTMLElement {
    connectedCallback() {
      this.textarea = this.querySelector('textarea');
      this.saveButton = this.querySelector('[data-gift-message-save]');
      this.status = this.querySelector('[data-gift-message-status]');
      const parsedProperties = JSON.parse(this.dataset.properties || '{}');
      const properties = Array.isArray(parsedProperties)
        ? Object.fromEntries(parsedProperties)
        : parsedProperties;

      this.properties = Object.fromEntries(
        Object.entries(properties).filter(([name]) => !/^\d+$/.test(name))
      );

      this.save = this.save.bind(this);
      this.markUnsaved = this.markUnsaved.bind(this);
      this.saveButton?.addEventListener('click', this.save);
      this.textarea?.addEventListener('input', this.markUnsaved);
    }

    disconnectedCallback() {
      this.saveButton?.removeEventListener('click', this.save);
      this.textarea?.removeEventListener('input', this.markUnsaved);
    }

    markUnsaved() {
      this.status.textContent = this.dataset.unsavedText;
    }

    getCurrentQuantity() {
      const quantityInput = this.closest('.cart-item')?.querySelector('input[name="updates[]"]:not([disabled])');
      return Number(quantityInput?.value || this.dataset.quantity);
    }

    async save() {
      if (this.savePromise) await this.savePromise;
      if (this.textarea.value === this.textarea.defaultValue) return true;

      const message = this.textarea.value;
      this.savePromise = this.saveMessage(message);
      const saved = await this.savePromise;
      this.savePromise = null;

      if (saved && this.textarea.value !== this.textarea.defaultValue) {
        return this.save();
      }

      return saved;
    }

    saveForPageExit() {
      if (this.textarea.value === this.textarea.defaultValue) return;

      fetch(FoxTheme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          id: this.dataset.lineKey,
          quantity: this.getCurrentQuantity(),
          properties: { ...this.properties, modtagerbesked: this.textarea.value }
        })
      });
    }

    async saveMessage(message) {
      this.saveButton.disabled = true;
      this.saveButton.classList.add('loading');
      this.status.textContent = this.dataset.savingText;

      try {
        const response = await fetch(FoxTheme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: this.dataset.lineKey,
            quantity: this.getCurrentQuantity(),
            properties: { ...this.properties, modtagerbesked: message }
          })
        });

        if (!response.ok) throw new Error('Gift message could not be saved');

        const cart = await response.json();
        const updatedItem = cart.items[Number(this.dataset.lineIndex)];

        if (!updatedItem) throw new Error('Updated cart item could not be found');

        this.dataset.lineKey = updatedItem.key;
        this.dataset.quantity = updatedItem.quantity;
        this.properties = updatedItem.properties || {};
        this.textarea.defaultValue = message;
        this.status.textContent = this.textarea.value === message ? this.dataset.savedText : this.dataset.unsavedText;
        this.saveButton.disabled = false;
        this.saveButton.classList.remove('loading');
        return true;
      } catch (error) {
        this.status.textContent = this.dataset.errorText;
        this.saveButton.disabled = false;
        this.saveButton.classList.remove('loading');
        console.error(error);
        return false;
      }
    }
  }

  customElements.define('gift-message-editor', GiftMessageEditor);
}

if (!window.__giftMessageGlobalListenersBound) {
  window.__giftMessageGlobalListenersBound = true;

document.addEventListener('submit', async (event) => {
  const form = event.target;

  if (!(form instanceof HTMLFormElement) || form.id !== 'cart' || form.dataset.giftMessagesSaved === 'true') {
    return;
  }

  const editors = [...document.querySelectorAll('gift-message-editor')];
  const hasUnsavedMessages = editors.some(
    (editor) => editor.savePromise || editor.textarea?.value !== editor.textarea?.defaultValue
  );

  if (!hasUnsavedMessages) return;

  event.preventDefault();
  const results = await Promise.all(editors.map((editor) => editor.save()));

  if (results.every(Boolean)) {
    form.dataset.giftMessagesSaved = 'true';
    form.requestSubmit(event.submitter);
  }
}, true);

document.addEventListener('click', async (event) => {
  const link = event.target.closest('a[href]');

  if (
    !link ||
    link.target === '_blank' ||
    link.hasAttribute('download') ||
    link.matches('.btn-remove, [is="cart-remove-item"]') ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const editors = [...document.querySelectorAll('gift-message-editor')];
  const hasUnsavedMessages = editors.some(
    (editor) => editor.savePromise || editor.textarea?.value !== editor.textarea?.defaultValue
  );

  if (!hasUnsavedMessages) return;

  event.preventDefault();
  const results = await Promise.all(editors.map((editor) => editor.save()));

  if (results.every(Boolean)) window.location.assign(link.href);
}, true);

window.addEventListener('pagehide', () => {
  document.querySelectorAll('gift-message-editor').forEach((editor) => editor.saveForPageExit());
});
}
